// Order sets (PROD-2712): the Sign step's question about orders still waiting for a diagnosis
// (board 6b: "Sign will ask you to link or remove these orders; nothing else waits"). It asks ONLY
// about those orders: one group per applied set, each with the set's suggested diagnosis, a link to
// a diagnosis already on the visit, or removal. When the last group is resolved the sign continues
// on its own (use-order-set-apply.ts); Cancel returns to the note with nothing signed.
import { Button, Modal } from '@scripsteam/scrips-signal-ds';
import type { DxCard } from '../lib/to-patient-assessment';
import type { OrderSetApplyState } from './use-order-set-apply';
import { plural } from './order-set-apply';

const CHIP_BUTTON =
  'inline-flex items-center h-8 py-0 px-3 border border-solid border-border-default rounded-sm bg-surface-card font-sans text-sm font-semibold text-brand-primary cursor-pointer whitespace-nowrap';

export function NeedsDxSignPrompt({ state, diagnoses }: { state: OrderSetApplyState; diagnoses: DxCard[] }) {
  if (!state.enabled || !state.signPromptOpen || state.needsDx.length === 0) return null;
  return (
    <Modal open onClose={state.cancelSignPrompt} title="Orders need a diagnosis" size="md">
      <div className="flex flex-col gap-4">
        <p className="m-0 text-sm text-text-primary">
          {`Link or remove ${plural(state.needsDxCount, 'order', 'orders')} before signing. Nothing else waits.`}
        </p>
        {state.needsDx.map((app) => (
          <section key={app.key} aria-label={`${app.setName}: orders waiting for a diagnosis`} className="flex flex-col gap-2">
            <span className="font-bold text-size-eyebrow uppercase text-brand-depth">{app.setName}</span>
            <ul className="m-0 pl-4 text-sm text-text-primary">
              {app.pending.map((d) => (
                <li key={d.planOrder.id}>{d.planOrder.label}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              {app.suggested && (
                <button type="button" className={CHIP_BUTTON} onClick={() => state.resolveNeedsDx(app.key, { kind: 'suggested' })}>
                  {`Use suggested ${app.suggested.code} ${app.suggested.display}`}
                </button>
              )}
              {diagnoses.map((dx) => (
                <button
                  key={dx.id}
                  type="button"
                  className={CHIP_BUTTON}
                  onClick={() => state.resolveNeedsDx(app.key, { kind: 'dx', dxId: dx.id })}
                >
                  {`Link to ${dx.code ? `${dx.code} ${dx.display}` : dx.display}`}
                </button>
              ))}
              <button type="button" className={CHIP_BUTTON} onClick={() => state.resolveNeedsDx(app.key, { kind: 'remove' })}>
                {app.pending.length === 1 ? 'Remove this order' : 'Remove these orders'}
              </button>
            </div>
          </section>
        ))}
        <div className="flex justify-end">
          <Button variant="tertiary" size="sm" onClick={state.cancelSignPrompt}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
